//TODO : Refactor after ai
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import type { OverviewObject } from "./types";
import { Attribute } from "./Attribute";
import { ObjectTypeBadge } from "./ObjectTypeBadge";
import { Badge, MapPin } from "./primitives";

export interface ObjectMapProps extends HTMLAttributes<HTMLDivElement> {
  items?: OverviewObject[];
  /** Real map tiles. Without them the surface renders a placeholder grid. */
  tiles?: ReactNode;
  onOpen?: (item: OverviewObject) => void;
  selectedId?: string;
  note?: string;
  height?: number | string;
  style?: CSSProperties;
}

/**
 * Map representation. Objects need `coords: { x, y }` as percentages of the
 * surface. No map tiles ship with this design system — supply real ones via
 * `tiles`, or the surface renders a neutral grid with a visible note, so a
 * placeholder is never mistaken for real geography.
 */
export function ObjectMap({
  items = [],
  tiles,
  onOpen,
  selectedId,
  note = "Keine Kartendaten hinterlegt — die Markierungen liegen auf einer Platzhalterfläche.",
  height,
  className = "",
  style,
  ...rest
}: ObjectMapProps) {
  const selected = items.find((i) => i.id === selectedId);

  return (
    <div className={`bui-ov-map ${className}`} style={{ height, ...style }} {...rest}>
      {tiles ? (
        <div className="bui-ov-map__tiles">{tiles}</div>
      ) : (
        <>
          <div className="bui-ov-map__grid" />
          <span className="bui-ov-map__note">{note}</span>
        </>
      )}

      {items
        .filter((item) => item.coords)
        .map((item) => (
          <span
            key={item.id}
            className="bui-ov-map__pin"
            style={{
              left: `${item.coords?.x}%`,
              top: `${item.coords?.y}%`,
              zIndex: item.id === selectedId ? 3 : 1,
            }}
            onClick={() => onOpen?.(item)}
          >
            <MapPin
              color={item.statusColor ?? "brand"}
              size={34}
              selected={item.id === selectedId}
            >
              {item.pinIcon}
            </MapPin>
          </span>
        ))}

      {selected && (
        <article
          style={{
            position: "absolute",
            right: 12,
            top: 12,
            width: 288,
            zIndex: 4,
            background: "var(--bg-primary)",
            borderRadius: "var(--r-l)",
            boxShadow: "inset 0 0 0 1px var(--neutral-200), var(--shadow-l)",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ObjectTypeBadge
              type={selected.type}
              identifier={selected.identifier}
              color={selected.typeColor ?? "brand"}
            />
            {selected.status && (
              <span style={{ marginLeft: "auto" }}>
                <Badge color={selected.statusColor} dot size="sm">
                  {selected.status}
                </Badge>
              </span>
            )}
          </div>
          <span
            style={{
              fontFamily: "var(--font-sans)",
              fontWeight: 600,
              fontSize: 16,
              lineHeight: "24px",
              color: "var(--text-primary)",
              textWrap: "pretty",
            }}
          >
            {selected.title}
          </span>
          {selected.subtitle && (
            <span style={{ font: "var(--t-paragraph-s)", color: "var(--text-tertiary)" }}>
              {selected.subtitle}
            </span>
          )}
          {(selected.attributes ?? []).slice(0, 2).map((a) => (
            <Attribute key={a.label} label={a.label} value={a.value} />
          ))}
        </article>
      )}
    </div>
  );
}
